import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add,
  Delete,
  Download,
  Edit,
  Groups,
  Refresh,
  Warning,
  InfoOutlined,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { apiService } from '../services/api';
import CrewSetupPanel from '../components/CrewSetupPanel';

const GREEN = '#2e7d32';
const DISCLAIMER =
  'Crew Management and payroll exports are for informational purposes only. They are not payroll, tax, or accounting software and do not replace a payroll provider or CPA.';

/** First day of current month → today (YYYY-MM-DD). */
function defaultMonthDateRange(): { from: string; to: string } {
  const now = new Date();
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const to = now.toISOString().slice(0, 10);
  return { from, to };
}

const MetricHeader: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} component="span">
    <span>{label}</span>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  </Stack>
);

type TabKey = 'dashboard' | 'employees' | 'crews' | 'timeclock' | 'productivity' | 'export';

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  payType: 'hourly' | 'daily';
  payRate: number;
  status: 'active' | 'inactive';
  hireDate?: string;
  notes?: string;
}

interface CrewTeam {
  _id: string;
  name: string;
  crewNumber?: number;
  colorTag?: string;
  employeeIds?: Employee[];
  crewLeaderEmployeeId?: Employee | string | null;
  crewUserId?: { firstName?: string; lastName?: string; email?: string; crewNumber?: number };
  todayEmployeeIds?: string[];
}

interface DashboardData {
  employeesActive: number;
  clockedIn: number;
  crewsWorkingToday: number;
  jobsCompletedToday: number;
  laborHoursToday: number;
  avgMinutesPerJob: number;
  flaggedShifts: number;
}

interface ProductivityRow {
  id: string;
  name: string;
  payType?: string;
  jobsCompleted: number;
  laborHours: number;
  revenueGenerated: number;
  estimatedLaborCost: number;
  revenuePerLaborHour: number | null;
  efficiency: number | null;
  laborCostPercentage: number | null;
  efficiencyJobCount: number;
}

interface ProductivityTrendPoint {
  weekStart: string;
  weekLabel: string;
  revenuePerLaborHour: number | null;
  efficiency: number | null;
  laborCostPercentage: number | null;
  jobsCompleted: number;
}

type ProductivityView = 'employee' | 'crew';

const emptyEmployeeForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  payType: 'hourly' as 'hourly' | 'daily',
  payRate: '',
  hireDate: '',
  notes: '',
  assignCrewId: '',
};

const ProviderCrewManagementPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [crews, setCrews] = useState<CrewTeam[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [productivity, setProductivity] = useState<ProductivityRow[]>([]);
  const [flaggedShifts, setFlaggedShifts] = useState<any[]>([]);

  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
  const [saving, setSaving] = useState(false);

  const monthDefault = defaultMonthDateRange();
  const [exportFrom, setExportFrom] = useState(monthDefault.from);
  const [exportTo, setExportTo] = useState(monthDefault.to);
  const [exporting, setExporting] = useState(false);
  const [prodFrom, setProdFrom] = useState(monthDefault.from);
  const [prodTo, setProdTo] = useState(monthDefault.to);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodView, setProdView] = useState<ProductivityView>('employee');
  const [prodBaselineReady, setProdBaselineReady] = useState(false);
  const [prodBaselineJobCount, setProdBaselineJobCount] = useState(0);
  const [selectedProdRow, setSelectedProdRow] = useState<ProductivityRow | null>(null);
  const [prodTrends, setProdTrends] = useState<ProductivityTrendPoint[]>([]);
  const [prodTrendsLoading, setProdTrendsLoading] = useState(false);

  const [crewDialog, setCrewDialog] = useState(false);
  const [editingCrew, setEditingCrew] = useState<CrewTeam | null>(null);
  const [crewRosterIds, setCrewRosterIds] = useState<string[]>([]);
  const [crewLeaderId, setCrewLeaderId] = useState<string>('');
  const [savingCrew, setSavingCrew] = useState(false);

  const MAX_CREW_SIZE = 4;

  const otherCrewNamesForEmployee = (employeeId: string, excludeCrewId?: string): string[] => {
    return crews
      .filter((c) => c._id !== excludeCrewId)
      .filter((c) =>
        (c.employeeIds || []).some((e: any) => (e._id || e)?.toString() === employeeId)
      )
      .map((c) => `${c.name}${c.crewNumber ? ` (#${c.crewNumber})` : ''}`);
  };

  const openEditCrew = (crew: CrewTeam) => {
    setEditingCrew(crew);
    const ids = (crew.employeeIds || []).map((e: any) => (e._id || e).toString());
    setCrewRosterIds(ids);
    const lead =
      typeof crew.crewLeaderEmployeeId === 'object' && crew.crewLeaderEmployeeId
        ? (crew.crewLeaderEmployeeId as Employee)._id
        : (crew.crewLeaderEmployeeId as string) || '';
    setCrewLeaderId(lead && ids.includes(lead) ? lead : '');
    setCrewDialog(true);
  };

  const toggleRosterMember = (employeeId: string) => {
    setCrewRosterIds((prev) => {
      if (prev.includes(employeeId)) {
        const next = prev.filter((id) => id !== employeeId);
        if (crewLeaderId === employeeId) setCrewLeaderId('');
        return next;
      }
      if (prev.length >= MAX_CREW_SIZE) {
        setError(`Maximum ${MAX_CREW_SIZE} employees per crew`);
        return prev;
      }
      return [...prev, employeeId];
    });
  };

  const saveCrew = async () => {
    if (!editingCrew) return;
    if (crewLeaderId && !crewRosterIds.includes(crewLeaderId)) {
      setError('Crew Leader must be on this crew roster');
      return;
    }
    setSavingCrew(true);
    setError('');
    try {
      // Name is owned by crew login credentials — only save roster + lead here
      const res = (await apiService.put(`/crew-management/crews/${editingCrew._id}`, {
        employeeIds: crewRosterIds,
        crewLeaderEmployeeId: crewLeaderId || null,
      })) as any;
      if (res.success) {
        setCrewDialog(false);
        setEditingCrew(null);
        setSuccess('Crew updated');
        await loadCrews();
      } else {
        setError(res.error || 'Failed to update crew');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to update crew');
    } finally {
      setSavingCrew(false);
    }
  };

  const leadIdOf = (crew: CrewTeam): string | null => {
    if (!crew.crewLeaderEmployeeId) return null;
    if (typeof crew.crewLeaderEmployeeId === 'object') {
      return (crew.crewLeaderEmployeeId as Employee)._id;
    }
    return String(crew.crewLeaderEmployeeId);
  };

  const loadDashboard = useCallback(async () => {
    const res = await apiService.get<DashboardData>('/crew-management/dashboard');
    if (res.success && res.data) setDashboard(res.data);
  }, []);

  const loadEmployees = useCallback(async () => {
    const res = await apiService.get<Employee[]>('/crew-management/employees');
    if (res.success && res.data) setEmployees(res.data);
  }, []);

  const loadCrews = useCallback(async () => {
    const res = await apiService.get<CrewTeam[]>('/crew-management/crews');
    if (res.success && res.data) setCrews(res.data);
  }, []);

  const loadShifts = useCallback(async () => {
    const res = await apiService.get<any[]>('/crew-management/shifts?period=current_week');
    if (res.success && res.data) setShifts(res.data);
  }, []);

  const loadProductivity = useCallback(async (from?: string, to?: string, view?: ProductivityView) => {
    const f = from ?? prodFrom;
    const t = to ?? prodTo;
    const v = view ?? prodView;
    if (!f || !t) return;
    if (f > t) {
      setError('Start date must be on or before end date');
      return;
    }
    setProdLoading(true);
    setSelectedProdRow(null);
    setProdTrends([]);
    try {
      const res = await apiService.get<{
        rows: ProductivityRow[];
        baselineReady: boolean;
        baselineJobCount: number;
      }>(
        `/crew-management/productivity?from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}&view=${v}`
      );
      if (res.success && res.data) {
        setProductivity(res.data.rows || []);
        setProdBaselineReady(res.data.baselineReady ?? false);
        setProdBaselineJobCount(res.data.baselineJobCount ?? 0);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load productivity');
    } finally {
      setProdLoading(false);
    }
  }, [prodFrom, prodTo, prodView]);

  const loadProdTrends = useCallback(async (row: ProductivityRow, view?: ProductivityView) => {
    const v = view ?? prodView;
    setProdTrendsLoading(true);
    try {
      const res = await apiService.get<{ trends: ProductivityTrendPoint[] }>(
        `/crew-management/productivity/trends?entityId=${encodeURIComponent(row.id)}&view=${v}&to=${encodeURIComponent(prodTo)}&weeks=12`
      );
      if (res.success && res.data?.trends) setProdTrends(res.data.trends);
    } catch (e: any) {
      setError(e.message || 'Failed to load productivity trends');
    } finally {
      setProdTrendsLoading(false);
    }
  }, [prodView, prodTo]);

  const selectProdRow = (row: ProductivityRow) => {
    setSelectedProdRow(row);
    loadProdTrends(row);
  };

  const loadFlagged = useCallback(async () => {
    const res = await apiService.get<any[]>('/crew-management/shifts/flagged');
    if (res.success && res.data) {
      const open = res.data.filter((s: any) => !s.routeEndedAt);
      setFlaggedShifts(open);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        loadDashboard(),
        loadEmployees(),
        loadCrews(),
        loadShifts(),
        loadFlagged(),
      ]);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [loadDashboard, loadEmployees, loadCrews, loadShifts, loadFlagged]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (tab === 'productivity') loadProductivity();
  }, [tab, loadProductivity]);

  const validateDateRange = (from: string, to: string): string | null => {
    if (!from || !to) return 'Select both start and end dates';
    if (from > to) return 'Start date must be on or before end date';
    return null;
  };

  const openAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm(emptyEmployeeForm);
    setEmployeeDialog(true);
  };

  const openEditEmployee = (emp: Employee) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      firstName: emp.firstName,
      lastName: emp.lastName,
      phone: emp.phone || '',
      email: emp.email || '',
      payType: emp.payType,
      payRate: String(emp.payRate),
      hireDate: emp.hireDate ? emp.hireDate.slice(0, 10) : '',
      notes: emp.notes || '',
      assignCrewId: '',
    });
    setEmployeeDialog(true);
  };

  const saveEmployee = async () => {
    setSaving(true);
    setError('');
    try {
      const { assignCrewId, ...rest } = employeeForm;
      const payload = {
        ...rest,
        payRate: Number(employeeForm.payRate),
      };
      const res = editingEmployee
        ? await apiService.put(`/crew-management/employees/${editingEmployee._id}`, payload)
        : await apiService.post('/crew-management/employees', payload);
      if (res.success) {
        const newId = (res.data as any)?._id;
        if (!editingEmployee && assignCrewId && newId) {
          const crew = crews.find((c) => c._id === assignCrewId);
          const existingIds = (crew?.employeeIds || []).map((e: any) => (e._id || e).toString());
          if (existingIds.length >= MAX_CREW_SIZE) {
            setSuccess('Employee added — crew is full (max 4). Assign from the Crews tab.');
          } else {
            const crewRes = (await apiService.put(`/crew-management/crews/${assignCrewId}`, {
              employeeIds: [...existingIds, String(newId)],
            })) as any;
            if (crewRes.success) {
              if (Array.isArray(crewRes.warnings) && crewRes.warnings.length) {
                setSuccess(`Employee added and assigned. Note: ${crewRes.warnings[0]}`);
              } else {
                setSuccess('Employee added and assigned to crew');
              }
            } else {
              setSuccess('Employee added — assign them from the Crews tab');
            }
          }
        } else {
          setSuccess(editingEmployee ? 'Employee updated' : 'Employee added — assign them on the Crews tab');
        }
        setEmployeeDialog(false);
        await loadEmployees();
        await loadCrews();
        await loadDashboard();
      } else {
        setError(res.error || 'Save failed');
      }
    } catch (e: any) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivateEmployee = async (id: string) => {
    if (!window.confirm('Remove this employee from Crew Management?')) return;
    const res = await apiService.delete(`/crew-management/employees/${id}`);
    if (res.success) {
      setSuccess('Employee removed');
      await loadEmployees();
    } else {
      setError(res.error || 'Delete failed');
    }
  };

  const closeFlaggedShift = async (id: string) => {
    const res = await apiService.post(`/crew-management/shifts/${id}/close`, {});
    if (res.success) {
      setSuccess('Shift closed');
      await loadFlagged();
      await loadShifts();
      await loadDashboard();
    } else {
      setError(res.error || 'Failed to close shift');
    }
  };

  const downloadPayroll = async () => {
    setExporting(true);
    setError('');
    try {
      const rangeErr = validateDateRange(exportFrom, exportTo);
      if (rangeErr) {
        setError(rangeErr);
        setExporting(false);
        return;
      }
      const token = localStorage.getItem('token');
      const apiBase = (import.meta as any).env?.VITE_API_URL || '/api';
      const params = new URLSearchParams({ from: exportFrom, to: exportTo });
      const url = `${apiBase}/crew-management/export/payroll?${params.toString()}`;
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || 'Export failed');
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'lawnhub-payroll-export.csv';
      link.click();
      setSuccess('Payroll CSV downloaded');
    } catch (e: any) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const statCard = (label: string, value: string | number, color?: string) => (
    <Grid item xs={6} sm={4} md={2} key={label}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: color || GREEN }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: GREEN }}>
            Crew Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set up crew logins and which services each crew handles, assign employees to crews, then track time and
            payroll-ready exports. Route Optimization uses your saved logins and service assignments each day.
          </Typography>
        </Box>
        <IconButton onClick={refreshAll} aria-label="Refresh">
          <Refresh />
        </IconButton>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        {DISCLAIMER}
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {flaggedShifts.length > 0 && (
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight={700} gutterBottom>
            {flaggedShifts.length} shift(s) need review — route may not have been ended.
          </Typography>
          {flaggedShifts.map((s: any) => (
            <Stack
              key={s._id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="body2">
                {(s.crewTeamId as any)?.name || 'Crew'} ·{' '}
                {s.routeStartedAt ? new Date(s.routeStartedAt).toLocaleString() : 'unknown start'}
                {s.flaggedReason ? ` — ${s.flaggedReason}` : ''}
              </Typography>
              <Button size="small" variant="contained" color="warning" onClick={() => closeFlaggedShift(s._id)}>
                Close Shift
              </Button>
            </Stack>
          ))}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="dashboard" label="Dashboard" />
        <Tab value="employees" label="Employees" />
        <Tab value="crews" label="Crews" />
        <Tab value="timeclock" label="Time Clock" />
        <Tab value="productivity" label="Productivity" />
        <Tab value="export" label="Payroll Export" />
      </Tabs>

      {tab === 'dashboard' && dashboard && (
        <Box>
          <Grid container spacing={2} mb={3}>
            {statCard('Active Employees', dashboard.employeesActive)}
            {statCard('Clocked In Now', dashboard.clockedIn)}
            {statCard('Crews Today', dashboard.crewsWorkingToday)}
            {statCard('Jobs Today', dashboard.jobsCompletedToday)}
            {statCard('Labor Hours', dashboard.laborHoursToday.toFixed(1))}
            {statCard('Avg Min/Job', dashboard.avgMinutesPerJob.toFixed(0))}
          </Grid>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Getting started
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              1. On Crews, use View &amp; Edit Crew Logins and Assign Services. 2. Add employees with pay rates.
              3. Open each crew card to set roster and optional Lead. 4. Use Route Optimization for Sort and Optimize.
              5. Export payroll CSV when ready.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" onClick={() => setTab('crews')}>
                Crew Logins &amp; Assign Services
              </Button>
              <Button variant="outlined" onClick={() => setTab('employees')}>
                Add Employees
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {tab === 'employees' && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={openAddEmployee} sx={{ bgcolor: GREEN }}>
              Add Employee
            </Button>
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Pay</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No employees yet. Add your first crew member.
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow key={emp._id}>
                      <TableCell>
                        {emp.firstName} {emp.lastName}
                      </TableCell>
                      <TableCell>{emp.payType === 'daily' ? 'Daily' : 'Hourly'}</TableCell>
                      <TableCell>${emp.payRate.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={emp.status}
                          color={emp.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditEmployee(emp)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deactivateEmployee(emp._id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 'crews' && (
        <Box>
          <CrewSetupPanel onCrewsChanged={loadCrews} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Click a crew to edit roster and optional Crew Leader. Crew name comes from View &amp; Edit Crew Logins.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            {crews.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info">
                  No crew teams yet. Choose a crew count above and click View &amp; Edit Crew Logins — team cards appear
                  automatically.
                </Alert>
              </Grid>
            ) : (
              crews.map((crew) => {
                const leadId = leadIdOf(crew);
                const loginLabel = crew.crewUserId
                  ? `Login: ${crew.crewUserId.firstName || ''} ${crew.crewUserId.lastName || ''}`.trim() ||
                    crew.crewUserId.email ||
                    (crew.crewNumber ? `crew-${crew.crewNumber}` : 'Crew login')
                  : crew.crewNumber
                    ? `Login: crew-${crew.crewNumber}`
                    : 'No crew login linked';

                return (
                  <Grid item xs={12} md={6} key={crew._id}>
                    <Card
                      sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                      onClick={() => openEditCrew(crew)}
                    >
                      <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Groups color="primary" />
                            <Typography variant="h6">
                              {crew.name}
                              {crew.crewNumber ? ` (#${crew.crewNumber})` : ''}
                            </Typography>
                          </Stack>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditCrew(crew);
                            }}
                            aria-label="Edit crew"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          {loginLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Roster (max {MAX_CREW_SIZE}):
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {(crew.employeeIds || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No employees yet — click to assign.
                            </Typography>
                          ) : (
                            (crew.employeeIds || []).map((e: any) => {
                              const id = (e._id || e).toString();
                              const name =
                                typeof e === 'object' ? `${e.firstName} ${e.lastName}` : e;
                              const isLead = leadId === id;
                              return (
                                <Chip
                                  key={id}
                                  size="small"
                                  color={isLead ? 'primary' : 'default'}
                                  label={isLead ? `${name} · Lead` : name}
                                />
                              );
                            })
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Box>
      )}

      {tab === 'timeclock' && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Crew</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Ended</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Jobs</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shifts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No shifts this week. Crews start routes from the crew app.
                  </TableCell>
                </TableRow>
              ) : (
                shifts.map((s) => (
                  <TableRow key={s._id}>
                    <TableCell>{new Date(s.shiftDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {(s.crewTeamId as any)?.name || '—'}
                    </TableCell>
                    <TableCell>
                      {s.routeStartedAt ? new Date(s.routeStartedAt).toLocaleTimeString() : '—'}
                    </TableCell>
                    <TableCell>
                      {s.routeEndedAt ? new Date(s.routeEndedAt).toLocaleTimeString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.status}
                        color={
                          s.status === 'active'
                            ? 'warning'
                            : s.status === 'flagged'
                              ? 'error'
                              : 'success'
                        }
                      />
                    </TableCell>
                    <TableCell>{s.jobsCompletedCount || 0}</TableCell>
                    <TableCell align="right">
                      {(s.status === 'active' || s.status === 'flagged') && (
                        <Button size="small" onClick={() => closeFlaggedShift(s._id)}>
                          Close Shift
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 'productivity' && (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }} flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={prodFrom}
              onChange={(e) => setProdFrom(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={prodTo}
              onChange={(e) => setProdTo(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={prodView}
              onChange={(_, v) => {
                if (v) {
                  setProdView(v);
                  loadProductivity(undefined, undefined, v);
                }
              }}
            >
              <ToggleButton value="employee">Employee</ToggleButton>
              <ToggleButton value="crew">Crew</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              onClick={() => loadProductivity()}
              disabled={prodLoading}
              startIcon={prodLoading ? <CircularProgress size={16} /> : <Refresh />}
            >
              Update
            </Button>
          </Stack>

          {!prodBaselineReady && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Efficiency needs at least 10 completed jobs with crew presence before this period.
              Revenue / Labor Hr and Labor Cost % are still shown. Baseline jobs found: {prodBaselineJobCount}.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click a row to see weekly trends for the last 12 weeks ending on your end date.
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{prodView === 'employee' ? 'Employee' : 'Crew'}</TableCell>
                  <TableCell>Jobs</TableCell>
                  <TableCell>
                    <MetricHeader
                      label="Revenue / Labor Hr"
                      tooltip="Attributed job revenue divided by on-job labor hours. Revenue is split when multiple crew members worked the same job. Higher is generally better."
                    />
                  </TableCell>
                  <TableCell>
                    <MetricHeader
                      label="Efficiency"
                      tooltip="Compares actual time on each job to expected time based on job revenue and your historical minutes-per-dollar before this period (100% = on par). Above 100% is faster than your typical pace for that price tier."
                    />
                  </TableCell>
                  <TableCell>
                    <MetricHeader
                      label="Labor Cost %"
                      tooltip="Estimated labor cost as a percentage of attributed job revenue. Uses pay rates and on-job time (or days with job activity for daily pay). Lower is generally better."
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prodLoading && productivity.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : productivity.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No productivity data for this period. Complete jobs with crew presence tracking to see metrics.
                    </TableCell>
                  </TableRow>
                ) : (
                  productivity.map((row) => {
                    const selected = selectedProdRow?.id === row.id;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={selected}
                        onClick={() => selectProdRow(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.jobsCompleted}</TableCell>
                        <TableCell>
                          {row.revenuePerLaborHour != null
                            ? `$${row.revenuePerLaborHour.toFixed(2)}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.efficiency != null ? `${row.efficiency.toFixed(0)}%` : '—'}
                        </TableCell>
                        <TableCell>
                          {row.laborCostPercentage != null
                            ? `${row.laborCostPercentage.toFixed(1)}%`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedProdRow && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Weekly trends — {selectedProdRow.name}
              </Typography>
              {prodTrendsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: 280 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Revenue / Labor Hr
                      </Typography>
                      <ResponsiveContainer width="100%" height="85%">
                        <LineChart data={prodTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="weekLabel" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                          <RechartsTooltip formatter={(v) => (v != null ? `$${Number(v).toFixed(2)}` : '—')} />
                          <Line type="monotone" dataKey="revenuePerLaborHour" stroke={GREEN} strokeWidth={2} dot={false} connectNulls name="Rev / Hr" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: 280 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Efficiency
                      </Typography>
                      <ResponsiveContainer width="100%" height="85%">
                        <LineChart data={prodTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="weekLabel" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
                          <RechartsTooltip formatter={(v) => (v != null ? `${Number(v).toFixed(0)}%` : '—')} />
                          <ReferenceLine y={100} stroke="#9e9e9e" strokeDasharray="4 4" />
                          <Line type="monotone" dataKey="efficiency" stroke={GREEN} strokeWidth={2} dot={false} connectNulls name="Efficiency" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: 280 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Labor Cost %
                      </Typography>
                      <ResponsiveContainer width="100%" height="85%">
                        <LineChart data={prodTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="weekLabel" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                          <RechartsTooltip formatter={(v) => (v != null ? `${Number(v).toFixed(1)}%` : '—')} />
                          <Line type="monotone" dataKey="laborCostPercentage" stroke={GREEN} strokeWidth={2} dot={false} connectNulls name="Labor Cost %" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Box>
      )}

      {tab === 'export' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payroll Export (CSV)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Exports total hours (hourly) or days worked (daily), rate, and gross pay for the dates you choose. Missing pay rates block export for that employee. No overtime or tax calculations.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={exportFrom}
              onChange={(e) => setExportFrom(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={exportTo}
              onChange={(e) => setExportTo(e.target.value)}
              sx={{ minWidth: 160 }}
            />
          </Stack>
          <Box>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Download />}
              disabled={exporting}
              onClick={downloadPayroll}
              sx={{ bgcolor: GREEN }}
            >
              Download Payroll CSV
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog open={employeeDialog} onClose={() => !saving && setEmployeeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              required
              value={employeeForm.firstName}
              onChange={(e) => setEmployeeForm({ ...employeeForm, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              required
              value={employeeForm.lastName}
              onChange={(e) => setEmployeeForm({ ...employeeForm, lastName: e.target.value })}
            />
            <FormControl>
              <InputLabel>Pay Type</InputLabel>
              <Select
                value={employeeForm.payType}
                label="Pay Type"
                onChange={(e) =>
                  setEmployeeForm({ ...employeeForm, payType: e.target.value as 'hourly' | 'daily' })
                }
              >
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Pay Rate ($)"
              type="number"
              required
              value={employeeForm.payRate}
              onChange={(e) => setEmployeeForm({ ...employeeForm, payRate: e.target.value })}
            />
            <TextField
              label="Phone"
              value={employeeForm.phone}
              onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
            />
            <TextField
              label="Email"
              value={employeeForm.email}
              onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
            />
            <TextField
              label="Hire Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={employeeForm.hireDate}
              onChange={(e) => setEmployeeForm({ ...employeeForm, hireDate: e.target.value })}
            />
            <TextField
              label="Notes"
              multiline
              rows={2}
              value={employeeForm.notes}
              onChange={(e) => setEmployeeForm({ ...employeeForm, notes: e.target.value })}
            />
            {!editingEmployee && crews.length > 0 && (
              <FormControl>
                <InputLabel>Assign to crew (optional)</InputLabel>
                <Select
                  value={employeeForm.assignCrewId}
                  label="Assign to crew (optional)"
                  onChange={(e) =>
                    setEmployeeForm({ ...employeeForm, assignCrewId: e.target.value })
                  }
                >
                  <MenuItem value="">None — assign later</MenuItem>
                  {crews.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                      {c.crewNumber ? ` (#${c.crewNumber})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeeDialog(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEmployee} disabled={saving} sx={{ bgcolor: GREEN }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={crewDialog}
        onClose={() => !savingCrew && setCrewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Crew
          {editingCrew?.crewNumber ? ` #${editingCrew.crewNumber}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingCrew && (
              <Alert severity="info">
                <strong>{editingCrew.name}</strong>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Rename this crew in View &amp; Edit Crew Logins. Username:{' '}
                  {editingCrew.crewUserId?.email ||
                    (editingCrew.crewNumber ? `crew-${editingCrew.crewNumber}` : 'not linked')}
                </Typography>
              </Alert>
            )}

            <Typography variant="subtitle2">Roster (max {MAX_CREW_SIZE})</Typography>
            {employees.filter((e) => e.status === 'active').length === 0 ? (
              <Alert severity="info">Add employees first, then assign them here.</Alert>
            ) : (
              <FormGroup>
                {employees
                  .filter((e) => e.status === 'active')
                  .map((emp) => {
                    const checked = crewRosterIds.includes(emp._id);
                    const others = otherCrewNamesForEmployee(emp._id, editingCrew?._id);
                    const atMax = !checked && crewRosterIds.length >= MAX_CREW_SIZE;
                    return (
                      <Box key={emp._id} sx={{ mb: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked}
                              disabled={atMax}
                              onChange={() => toggleRosterMember(emp._id)}
                            />
                          }
                          label={`${emp.firstName} ${emp.lastName}`}
                        />
                        {others.length > 0 && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                            sx={{ ml: 4, mt: -0.5 }}
                          >
                            Also on: {others.join(', ')}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
              </FormGroup>
            )}

            <Typography variant="subtitle2">Crew Leader (optional)</Typography>
            <Typography variant="caption" color="text.secondary">
              Who to call if something comes up — not who must use the app.
            </Typography>
            <RadioGroup
              value={crewLeaderId}
              onChange={(e) => setCrewLeaderId(e.target.value)}
            >
              <FormControlLabel value="" control={<Radio />} label="No designated lead" />
              {crewRosterIds.map((id) => {
                const emp = employees.find((e) => e._id === id);
                if (!emp) return null;
                return (
                  <FormControlLabel
                    key={id}
                    value={id}
                    control={<Radio />}
                    label={`${emp.firstName} ${emp.lastName}`}
                  />
                );
              })}
            </RadioGroup>

            {crewRosterIds.some(
              (id) => otherCrewNamesForEmployee(id, editingCrew?._id).length > 0
            ) && (
              <Alert severity="warning">
                {crewRosterIds
                  .map((id) => {
                    const emp = employees.find((e) => e._id === id);
                    const others = otherCrewNamesForEmployee(id, editingCrew?._id);
                    if (!emp || !others.length) return null;
                    return `${emp.firstName} ${emp.lastName} is also on ${others.join(
                      ', '
                    )}. OK if they do not work both crews the same day.`;
                  })
                  .filter(Boolean)
                  .map((text, i) => (
                    <Typography key={i} variant="body2">
                      {text}
                    </Typography>
                  ))}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrewDialog(false)} disabled={savingCrew}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveCrew}
            disabled={savingCrew}
            sx={{ bgcolor: GREEN }}
          >
            {savingCrew ? 'Saving…' : 'Save Crew'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderCrewManagementPage;
